import { useState, useEffect } from "react";
import { useApp } from "../context/AppContext";
import { upsertProfile } from "../lib/db";
import supabase from "../lib/supabase";


  

const ROLES = [
  { id: "engineer",    label: "Engineering",         icon: "⚙️" },
  { id: "designer",    label: "Design & Creative",   icon: "🎨" },
  { id: "pm",          label: "Product & Strategy",  icon: "📦" },
  { id: "data",        label: "Data & Analytics",    icon: "📊" },
  { id: "marketing",   label: "Marketing & Growth",  icon: "📣" },
  { id: "sales",       label: "Sales & BizDev",      icon: "💼" },
  { id: "operations",  label: "Operations & HR",     icon: "🏢" },
  { id: "finance",     label: "Finance & Accounting", icon: "💰" },
  { id: "healthcare",  label: "Healthcare",          icon: "🏥" },
  { id: "legal",       label: "Legal & Compliance",  icon: "⚖️" },
  { id: "education",   label: "Education",           icon: "📚" },
  { id: "other",       label: "Other",               icon: "✦"  },
];

const GOALS = [
  { id: "job",     label: "Land a new job",      icon: "🎯" },
  { id: "comp",    label: "Better compensation", icon: "💰" },
  { id: "explore", label: "Explore options",     icon: "🔍" },
  { id: "network", label: "Grow my network",     icon: "🤝" },
];

export default function OnboardingPage({ onComplete }) {
  const { user, setProfile } = useApp();
  const [step,        setStep]        = useState(0);
  const [data,        setData]        = useState({});
  const [saving,      setSaving]      = useState(false);
  const [teamInvite,  setTeamInvite]  = useState(null); // pending team invite
  const [checkingTeam,setCheckingTeam]= useState(true);

  const up = (k, v) => setData(p => ({ ...p, [k]: v }));

  // ── Check if user was invited to an employer team ─────────────────────────
  useEffect(() => {
    if (!user?.id || !user?.email) { setCheckingTeam(false); return; }
    const checkInvite = async () => {
      try {
        const { data: invite } = await supabase
          .from("employer_teams")
          .select("*, employer:profiles!employer_teams_employer_id_fkey(full_name)")
          .eq("invite_email", user.email.toLowerCase())
          .is("member_id", null)
          .single();

        if (invite) {
          setTeamInvite(invite);
          // Auto-accept: update member_id and set role to employer
          await supabase.from("employer_teams")
            .update({ member_id: user.id })
            .eq("id", invite.id);
          await supabase.from("profiles")
            .update({ role:"employer", onboarded:true })
            .eq("id", user.id);
        }
      } catch {} // no invite found — normal flow
      finally { setCheckingTeam(false); }
    };
    checkInvite();
  }, [user?.id, user?.email]);

  const finish = async () => {
    if ((data.goals || []).length === 0) return;
    setSaving(true);
    try {
      if (user) {
        const { data: profile } = await upsertProfile(user.id, {
          full_name: user.user_metadata?.full_name || data.name || "User",
          role:      data.role,
          goals:     data.goals,
          headline:  data.headline || "",
          onboarded: true,
        });
        if (profile) setProfile(profile);
      }
      onComplete(data);
    } catch (err) {
      console.error("Onboarding save error:", err);
      onComplete(data);
    } finally {
      setSaving(false);
    }
  };

  const acceptTeamInvite = async () => {
    setSaving(true);
    try {
      const { data: profile } = await upsertProfile(user.id, {
        full_name: user.user_metadata?.full_name || "Team Member",
        role:      "employer",
        onboarded: true,
      });
      if (profile) setProfile(profile);
      onComplete({ role:"employer" });
    } catch(e) {
      console.error(e);
      onComplete({ role:"employer" });
    } finally { setSaving(false); }
  };

  const btnBase = (active) => ({
    background: active ? "var(--accent)" : "var(--bg-hover)",
    border: "none",
    color: active ? "#fff" : "var(--text-tertiary)",
    padding: "11px 28px", borderRadius: 10,
    fontWeight: 700, cursor: active ? "pointer" : "default",
    fontFamily: "'DM Sans',sans-serif", fontSize: 14,
  });

  // Loading while checking team invite
  if (checkingTeam) return (
    <div style={{ minHeight:"100vh", background:"var(--bg-primary)", display:"flex", alignItems:"center", justifyContent:"center" }}>
      <div style={{ width:28, height:28, border:"2.5px solid rgba(123,111,232,0.2)", borderTopColor:"#7B6FE8", borderRadius:"50%", animation:"spin 0.9s linear infinite" }} />
    </div>
  );

  // Team invite screen
  if (teamInvite) return (
    <div style={{ minHeight:"100vh", background:"var(--bg-primary)", display:"flex", alignItems:"center", justifyContent:"center", padding:24 }}>
      <div style={{ position:"fixed", inset:0, backgroundImage:"linear-gradient(var(--border-soft) 1px,transparent 1px),linear-gradient(90deg,var(--border-soft) 1px,transparent 1px)", backgroundSize:"48px 48px", opacity:0.5, pointerEvents:"none" }} />
      <div style={{ position:"relative", zIndex:1, background:"var(--bg-card)", border:"1px solid var(--border)", borderRadius:20, padding:"40px 36px", maxWidth:480, width:"100%", textAlign:"center" }}>
        <div style={{ fontSize:48, marginBottom:20 }}>🏢</div>
        <div style={{ fontSize:11, fontWeight:700, color:"var(--accent)", letterSpacing:2, textTransform:"uppercase", marginBottom:12 }}>Team Invitation</div>
        <h2 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:28, fontWeight:700, color:"var(--text-primary)", marginBottom:10, lineHeight:1.2 }}>
          You've been invited to join<br />
          <em style={{ fontStyle:"italic", color:"var(--accent)" }}>{teamInvite.employer?.full_name || "an employer"}</em>
        </h2>
        <p style={{ fontSize:14, color:"var(--text-secondary)", lineHeight:1.7, marginBottom:28 }}>
          You'll have access to their employer portal on Vorsia — including job postings, applicants, and hiring tools.
        </p>
        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
          <button onClick={acceptTeamInvite} disabled={saving}
            style={{ background:"var(--accent)", border:"none", color:"#fff", padding:"14px", borderRadius:11, fontWeight:700, fontSize:15, cursor:"pointer", fontFamily:"'DM Sans',sans-serif", opacity:saving?0.7:1 }}>
            {saving ? "Joining…" : "Accept & Enter Portal →"}
          </button>
          <button onClick={() => { setTeamInvite(null); }}
            style={{ background:"none", border:"none", color:"var(--text-tertiary)", cursor:"pointer", fontFamily:"'DM Sans',sans-serif", fontSize:13 }}>
            I'm a job seeker, not an employer
          </button>
        </div>
      </div>
    </div>
  );

  const steps = [
    // ── Welcome ──
    <div style={{ textAlign: "center", maxWidth: 520, margin: "0 auto" }}>
      <img src="/vorsia_ai.png" alt="Vorsia" style={{ width: 280, height: "auto", display: "block", margin: "0 auto 20px", animation: "bounceIn 0.5s cubic-bezier(0.34,1.56,0.64,1)" }} />
      <h1 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "clamp(36px,6vw,58px)", fontWeight: 700, lineHeight: 1.05, color: "var(--text-primary)", marginBottom: 16 }}>
        Welcome to<br /><em style={{ fontStyle: "italic", color: "var(--accent)" }}>Vorsia.</em>
      </h1>
      <p style={{ color: "var(--text-secondary)", fontSize: 16, lineHeight: 1.7, marginBottom: 32 }}>
        Your AI-Powered Career Platform. Set up in under 60 seconds.
      </p>
      <button onClick={() => setStep(1)} style={{ background: "linear-gradient(135deg, #29ABE2, #9B35C8)", border: "none", color: "#fff", padding: "14px 40px", borderRadius: 12, fontWeight: 700, fontSize: 16, cursor: "pointer", fontFamily: "'DM Sans',sans-serif" }}>
        Get Started →
      </button>
    </div>,

    // ── Discipline ──
    <div style={{ maxWidth: 640, margin: "0 auto" }}>
      <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 36, fontWeight: 700, color: "var(--text-primary)", marginBottom: 8, textAlign: "center" }}>What's your field?</h2>
      <p style={{ color: "var(--text-secondary)", marginBottom: 24, textAlign: "center", fontSize: 14 }}>We'll tailor roles and insights to your career.</p>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginBottom: 24 }}>
        {ROLES.map(r => (
          <button key={r.id} onClick={() => up("role", r.id)}
            style={{ padding: "14px 16px", borderRadius: 13, cursor: "pointer", fontFamily: "'DM Sans',sans-serif", border: `1.5px solid ${data.role === r.id ? "var(--accent)" : "var(--border)"}`, background: data.role === r.id ? "var(--accent-soft)" : "var(--bg-card)", textAlign: "left", transition: "all 0.18s", display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 20, flexShrink: 0 }}>{r.icon}</span>
            <span style={{ fontSize: 13, fontWeight: 600, color: data.role === r.id ? "var(--text-primary)" : "var(--text-secondary)", lineHeight: 1.3 }}>{r.label}</span>
          </button>
        ))}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <button onClick={() => setStep(0)} style={{ color: "var(--text-tertiary)", background: "none", border: "none", cursor: "pointer", fontFamily: "'DM Sans',sans-serif", fontSize: 13 }}>← Back</button>
        <button onClick={() => data.role && setStep(2)} style={btnBase(!!data.role)}>Continue →</button>
      </div>
    </div>,

    // ── Goals ──
    <div style={{ maxWidth: 560, margin: "0 auto" }}>
      <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 36, fontWeight: 700, color: "var(--text-primary)", marginBottom: 8, textAlign: "center" }}>What are you here for?</h2>
      <p style={{ color: "var(--text-secondary)", marginBottom: 24, textAlign: "center", fontSize: 14 }}>Select all that apply.</p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 24 }}>
        {GOALS.map(g => {
          const sel = (data.goals || []).includes(g.id);
          const toggle = () => up("goals", sel ? (data.goals || []).filter(x => x !== g.id) : [...(data.goals || []), g.id]);
          return (
            <button key={g.id} onClick={toggle}
              style={{ padding: "16px", borderRadius: 13, cursor: "pointer", fontFamily: "'DM Sans',sans-serif", border: `1.5px solid ${sel ? "#4ade80" : "var(--border)"}`, background: sel ? "rgba(74,222,128,0.08)" : "var(--bg-card)", textAlign: "left", position: "relative", transition: "all 0.18s" }}>
              {sel && (
                <div style={{ position: "absolute", top: 10, right: 10, width: 18, height: 18, borderRadius: "50%", background: "#4ade80", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, color: "#0a0a0f", fontWeight: 700 }}>✓</div>
              )}
              <div style={{ fontSize: 24, marginBottom: 8 }}>{g.icon}</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: sel ? "var(--text-primary)" : "var(--text-secondary)" }}>{g.label}</div>
            </button>
          );
        })}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <button onClick={() => setStep(1)} style={{ color: "var(--text-tertiary)", background: "none", border: "none", cursor: "pointer", fontFamily: "'DM Sans',sans-serif", fontSize: 13 }}>← Back</button>
        <button onClick={finish} disabled={saving || (data.goals || []).length === 0}
          style={{ ...btnBase((data.goals || []).length > 0 && !saving), display: "flex", alignItems: "center", gap: 8 }}>
          {saving ? (
            <><div style={{ width: 13, height: 13, border: "2px solid var(--border)", borderTopColor: "#fff", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />Saving…</>
          ) : "Enter Vorsia →"}
        </button>
      </div>
    </div>,
  ];

  const progress = (step / (steps.length - 1)) * 100;

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg-primary)", display: "flex", alignItems: "center", justifyContent: "center", position: "relative" }}>
      <div style={{ position: "fixed", inset: 0, backgroundImage: "linear-gradient(var(--border-soft) 1px,transparent 1px),linear-gradient(90deg,var(--border-soft) 1px,transparent 1px)", backgroundSize: "48px 48px", opacity: 0.5, pointerEvents: "none" }} />
      <div style={{ position: "fixed", width: 600, height: 600, borderRadius: "50%", background: "radial-gradient(circle,rgba(232,97,58,0.06),transparent 70%)", top: -150, right: -100, pointerEvents: "none" }} />
      {step > 0 && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, height: 3 }}>
          <div style={{ height: "100%", width: `${progress}%`, background: "linear-gradient(135deg, #29ABE2, #9B35C8)", transition: "width 0.4s ease", borderRadius: 2 }} />
        </div>
      )}
      <div style={{ position: "relative", zIndex: 1, padding: "40px 24px", width: "100%", maxWidth: 740 }}>
        <div key={step} style={{ animation: "stepIn 0.4s cubic-bezier(0.34,1.56,0.64,1)" }}>
          {steps[step]}
        </div>
      </div>
    </div>
  );
}
